import httpClient from "../http/http-client";
import { IPostRule, IRule } from '../rule/rule.model';
import { sanitizeRulePayload } from '../rule/rule.service';
import {
  ILibraryPushResult,
  ILibraryRule,
  ILibraryRuleDeleteResult,
  ILibraryRuleImportClient,
  ILibraryRuleImportResult,
  ILibraryRuleImportSelection,
  ILibraryRulePushTarget,
  ILibraryRetractResult,
} from './library-rule.model';

// Library rules are authored with the same dialog as a client's own rules, so they take the
// same IPostRule body and need the same '' -> null UUID sanitising.
export default class LibraryRuleService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getAllWithPagination(page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', nameIncludes?: string, type?: string): Promise<{ count: number, rows: ILibraryRule[] }> {
    const params = new URLSearchParams({ page: String(page), perPage: String(perPage) });
    if (order && orderBy) { params.append('orderBy', orderBy); params.append('order', order); }
    if (nameIncludes) params.append('nameIncludes', nameIncludes);
    if (type) params.append('type', type);
    return this.http.get(`partner/library/rules?${params.toString()}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  // Typed as IRule so it can be handed straight to RuleDialog's fetchRule prop -- the shape
  // the dialog reads is identical.
  async getOne(libraryRuleId: string): Promise<IRule> {
    return this.http.get(`partner/library/rules/${libraryRuleId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async create(rule: IPostRule): Promise<ILibraryRule> {
    return this.http.post(`partner/library/rules`, sanitizeRulePayload(rule))
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async edit(libraryRuleId: string, rule: IPostRule): Promise<ILibraryRule> {
    return this.http.patch(`partner/library/rules/${libraryRuleId}`, sanitizeRulePayload(rule))
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // removeFromClients also takes the rule back out of the client accounts it was pushed to.
  // Copies already used on committed emissions data can't be deleted, so those are released
  // to the client instead and reported in retractResults.
  async delete(libraryRuleId: string, removeFromClients: boolean = false): Promise<ILibraryRuleDeleteResult> {
    return this.http.delete(`partner/library/rules/${libraryRuleId}`, { data: { removeFromClients } })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Take the rule out of specific client accounts, leaving the library rule in place.
  async retract(libraryRuleId: string, clientOrganisationIds: string[]): Promise<ILibraryRetractResult> {
    return this.http.post(`partner/library/rules/${libraryRuleId}/retract`, { clientOrganisationIds })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async setClientEditPermission(libraryRuleId: string, allowClientEdit: boolean): Promise<ILibraryRule> {
    return this.http.patch(`partner/library/rules/${libraryRuleId}/client-edit`, { allowClientEdit })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Every client of this partner, what state their copy is in, and whether a push would be
  // skipped for them. Computed by the same decision function the push itself uses.
  async getPushTargets(libraryRuleId: string): Promise<{ clients: ILibraryRulePushTarget[] }> {
    return this.http.get(`partner/library/rules/${libraryRuleId}/targets`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // overwriteClientEditIds opts specific clients in to having their own edits replaced.
  // Anything not listed keeps the default of being skipped and reported.
  async push(libraryRuleId: string, clientOrganisationIds: string[], overwriteClientEditIds: string[] = []): Promise<ILibraryPushResult> {
    return this.http.post(`partner/library/rules/${libraryRuleId}/push`, { clientOrganisationIds, overwriteClientEditIds })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Every rule this partner's clients hold, with what importing each one would do. Worked
  // out by the same decision function the import itself runs, so the preview and the outcome
  // cannot disagree.
  async getImportableRules(): Promise<{ clients: ILibraryRuleImportClient[] }> {
    return this.http.get(`partner/library/rules/importable`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // Pull the chosen rules up into the library. Each client's own rule is then linked to what
  // was created, so it reads as already pushed and stays exactly where it is.
  async importFromClients(selections: ILibraryRuleImportSelection[]): Promise<ILibraryRuleImportResult> {
    return this.http.post(`partner/library/rules/import`, { selections })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }
}
